import { Drawer, Box, ListItemButton, ListItemIcon, ListItemText, Stack, Typography, SxProps, Badge, Tooltip, Fade } from '@mui/material'
import { Features } from 'c1gen/features'
import { Roles } from 'c1gen/roles'
import { appBarHeight, C1IconButton, sidebarCollapsedWidth, sidebarWidth } from 'c1ui'
import { C1Transitions } from 'c1ui/theme/C1Transitions'
import { C1Dot } from 'components/core/dot'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { useHasAnyBuiltinRole, useHasBuiltinRole, useRoleChecker, useUnsafeFeatureChecker } from 'components/no-ui/capability'
import { useLocalStorageContext } from 'hooks/local-storage-context'
import { useSessionStorageContext } from 'hooks/session-storage-context'
import { MutedDotsCacheKey, RoleID } from 'lib/constants/system'
import { Route } from 'lib/routes'
import { ArrowLeft, ArrowRight, ChevronRight, LucideIcon } from 'lucide-react'
import { useRouter } from 'next/router'
import { FC, useEffect, useRef, useState, RefObject, useLayoutEffect, useCallback } from 'react'
import { useIntl } from 'react-intl'

export const COLLAPSED_SIDEBAR_NAV_STORAGE_KEY = 'c1-collapsed-sidebar-nav'

export const sidebarTransitionDuration = '360ms'

const subNavWidth = `calc(${sidebarWidth} + 40px)`

const navSurroundingPadding = 3
const navCollapsedSurroundingPadding = 2

const sidebarTransitionEasing = C1Transitions.easing.linearSpringA

export type NavItemProps = {
    id: string,
    label: string
    pathname: Route
    icon?: LucideIcon
    subMenu?: Array<NavItemProps>
    roles?: Array<Roles>
    features?: Array<Features>
    hideOnFeatures?: Array<Features>
    count?: number
    collapsed?: boolean
    isTopLevel?: boolean
    isNewSection?: boolean
    selectedPaths?: string[]
    warningIndicator?: React.ComponentType
}

type CollapsingSidebarProps = {
    items: NavItemProps[]
}

export const getTransition = (
    properties: string,
    duration: string = sidebarTransitionDuration,
    easing: string = sidebarTransitionEasing,
): SxProps => ({
    transitionProperty: properties,
    transitionDuration: duration,
    transitionTimingFunction: easing, // if the browser doesn't support linear(), this should just be ignored
})

type MutedDotCacheHookReturn = [boolean, (newValue: boolean) => void];

export const useMutedDotCache = (key: string): MutedDotCacheHookReturn => {
    const [cache, setCache] = useSessionStorageContext(MutedDotsCacheKey, {})

    const value = cache ? cache[key] : false

    const setValue = useCallback((newValue: boolean): void => {
        setCache({ ...cache, [key]: newValue })
    }, [cache, key, setCache])

    return [value, setValue]
}

export const CollapsingSidebar: FC<CollapsingSidebarProps> = ({ items }) => {
    const [isCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const [subNavForceHidden, setSubNavForceHidden] = useState(false)

    // For styling
    const width = isCollapsed ? sidebarCollapsedWidth : sidebarWidth
    const padding = isCollapsed ? navCollapsedSurroundingPadding : navSurroundingPadding

    return (
        <Drawer
            variant="permanent"
            PaperProps={{
                id: 'sidebar',
                sx: {
                    overflow: 'visible',
                    pt: appBarHeight,
                    zIndex: 10,
                    width,
                    ...getTransition('width'),
                    borderRight: '1px solid',
                    borderRightColor: 'border.subtle',
                    backgroundColor: 'background.default',
                    height: '100vh',
                },
            }}
            sx={{ width, ...getTransition('width') }}
            onMouseEnter={(): void => setSubNavForceHidden(false)}
        >
            <Box
                py={padding}
                sx={{
                    position: 'relative',
                    ...getTransition('padding'),
                }}
            >
                <CollapsingSidebarToggleButton />
                {items.map((item) => (
                    <CollapsingSidebarNavItem
                        item={item}
                        key={item.pathname}
                        setSubNavForceHidden={setSubNavForceHidden}
                        subNavForceHidden={subNavForceHidden}
                    />
                ))}
            </Box>
        </Drawer>
    )
}

type CollapsingSidebarNavItemDisplayProps = {
    item: NavItemProps
    isSubNavItem?: boolean
    subNavForceHidden?: boolean
    setSubNavForceHidden: (forceHidden: boolean) => void
}

const CollapsingSidebarNavItem: FC<CollapsingSidebarNavItemDisplayProps> = ({
    item,
    isSubNavItem,
    setSubNavForceHidden,
    subNavForceHidden,
}) => {
    const hasRole = useRoleChecker()
    const hasFeature = useUnsafeFeatureChecker()
    const router = useRouter()
    const hasAppAdminRole = useHasBuiltinRole(RoleID.ApplicationAdmin)
    const hasAnyConnectorAdminOrSuperAdminRoles = useHasAnyBuiltinRole([
        RoleID.IntegrationAdmin,
        RoleID.SuperAdmin,
        RoleID.ReadOnlySuperAdmin,
    ])

    const [_isCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const selectedPaths = [item.pathname, ...(item.selectedPaths || [])]

    const ref = useRef<HTMLDivElement>(null)

    const isCollapsed = isSubNavItem ? false : _isCollapsed

    const isSelected = selectedPaths.some((path) => {
        const regex = RegExp(`^\\${path}.*$`, 'g')
        return (router.pathname.match(regex) !== null && path !== '/')
            || (router.asPath.match(regex) !== null && path !== '/')
            || (path === '/' && router.pathname === '/')
    })

    const [dotIsMuted, setDotIsMuted] = useMutedDotCache(item.id)

    const prevCount = useRef(item.count)

    const padding = isCollapsed ? navCollapsedSurroundingPadding : navSurroundingPadding

    const [subNavRef, setSubNavRef] = useState<HTMLDivElement | null>(null)

    useEffect(() => {
        if (item.count === undefined) return

        if (prevCount.current && prevCount.current < item.count) {
            setDotIsMuted(false)
        }

        prevCount.current = item.count
    }, [item.count, item.id, setDotIsMuted])

    const shouldShowDot = (item.count !== undefined && item.count > 0) && !dotIsMuted

    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)

    const shouldRender = (navItemProps: NavItemProps): boolean => {
        // If the user is an app admin, they should not see the connectors nav item
        if (navItemProps.id === 'administer_connectors' && hasAppAdminRole && !hasAnyConnectorAdminOrSuperAdminRoles) return false
        // If any features are specified, the user must have any of them
        const meetsFF = navItemProps.features?.length ? navItemProps.features.some(hasFeature) : true
        const meetsRole = navItemProps.roles?.length ? navItemProps.roles.some(hasRole) : true
        const nohideOnFeatures = navItemProps.hideOnFeatures?.length ? !navItemProps.hideOnFeatures.some(hasFeature) : true
        return meetsFF && nohideOnFeatures && (isReadOnlySuperAdmin || meetsRole)
    }

    const onClick = (): void => {
        setDotIsMuted(true)
        isSubNavItem && setSubNavForceHidden(true)
    }

    const [mouseIsActive, setMouseIsActive] = useState(false)
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })

    const timerRef = useRef<number | null>(null)

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (!mouseIsActive) {
            setMouseIsActive(true)
        }

        if (timerRef.current) {
            window.clearTimeout(timerRef.current)
        }

        timerRef.current = window.setTimeout(() => {
            setMouseIsActive(false)
            timerRef.current = null
        }, 300)

        setMousePosition({ x: e.clientX, y: e.clientY })
    }

    useEffect(() => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current)
        }
    }, [])

    if (!shouldRender(item)) return null

    return <Box
        className={isSubNavItem ? 'subnavHoverParent' : 'hoverParent'}
        sx={{ mt: item.isNewSection ? 4 : 0, px: padding }}
        ref={ref}
        component="div"
        onMouseMove={item.subMenu ? handleMouseMove : undefined}
    >
        {item.subMenu && !subNavForceHidden && <>
            <CollapsingSidebarBackdrop />

            <Box sx={{
                visibility: 'hidden',
                position: 'relative',
                px: padding,
                mx: -padding,

                '.hoverParent:hover &': {
                    visibility: 'visible',
                    zIndex: 10,
                },
            }}
            >
                <CollapsingSidebarSubNav navItem={item} setSubNavRef={setSubNavRef} setSubNavForceHidden={setSubNavForceHidden} />

                {mouseIsActive && <CollapsingSidebarSafeArea hoverItemRef={ref} subNavRef={subNavRef} mousePosition={mousePosition} />}
            </Box>

        </>}

        <Tooltip
            title={item.label}
            placement="right"
            disableHoverListener={!isCollapsed || !!item.subMenu}
            enterDelay={0}
            leaveDelay={0}
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 0 }}
        >
            <ListItemButton
                alignItems="center"
                href={item.subMenu ? undefined : item.pathname}
                component={item.subMenu ? 'button' : C1Link}
                onClick={onClick}
                sx={{
                    p: 0,
                    py: isCollapsed ? '0.45rem' : '0.15rem',
                    backgroundColor: 'transparent',
                    width: isCollapsed ? '30px' : 'auto',
                    position: 'relative',
                    ...getTransition('padding, margin'),

                    '&:hover': {
                        backgroundColor: 'transparent',
                        zIndex: 30,
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        borderRadius: 1,
                        py: 1,
                        px: isCollapsed ? 1 : 2,
                        width: `calc(100% - ${padding}px)`,
                        backgroundColor: isSelected ? 'primary.light' : 'transparent',
                        ...getTransition('padding, margin'),

                        [`${isSubNavItem ? '.subnavHoverParent:hover &' : '.hoverParent:hover &'}`]: {
                            backgroundColor: isSelected ? 'primary.light' : 'primary.extraLight',
                        },
                    }}
                >
                    {item.icon && <ListItemIcon
                        sx={{
                            minWidth: 0,
                            pr: 2,
                            pl: isCollapsed ? '0.10rem' : 0,
                        }}
                    >
                        <Badge color="warning" variant="dot" invisible={!(shouldShowDot && isCollapsed)}>
                            <C1Icon icon={item.icon} sx={{ color: isSelected ? 'primary.extraDark' : 'text.primary' }} />
                        </Badge>
                    </ListItemIcon>}

                    <Stack
                        direction="row"
                        flexGrow={!isSubNavItem ? 0 : 1}
                        flexShrink={!isSubNavItem ? 0 : 1}
                        alignItems="center"
                        sx={{
                            width: !isSubNavItem ? '134px' : 'auto',
                            opacity: isCollapsed ? 0 : 1,
                            pointerEvents: isCollapsed ? 'none' : 'auto',
                            ...getTransition('opacity'),
                        }}
                    >
                        <ListItemText
                            sx={{ margin: 0 }}
                            primary={
                                <Typography
                                    component="div"
                                    color={isSelected ? 'primary.dark' : 'text.primary'}
                                    fontWeight={isSelected ? 500 : 450}
                                >
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            {item.label}
                                        </Box>
                                        <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                                            {shouldShowDot && <Box mr={1}><C1Dot /></Box>}
                                            {item.warningIndicator && <item.warningIndicator />}
                                        </Stack>
                                    </Stack>
                                </Typography>
                            }
                        />

                        {item.subMenu && (
                            <C1Icon icon={ChevronRight} sx={{ color: isSelected ? 'primary.extraDark' : 'text.primary' }} />
                        )}
                    </Stack>
                </Stack>
            </ListItemButton>
        </Tooltip>
    </Box>
}

type CollapsingSidebarSubNavProps = {
    navItem: NavItemProps
    setSubNavRef: (ref: HTMLDivElement) => void
    setSubNavForceHidden: (forceHidden: boolean) => void
}

export const CollapsingSidebarSubNav: FC<CollapsingSidebarSubNavProps> = ({
    navItem,
    setSubNavRef,
    setSubNavForceHidden,
}) => {
    const [isCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const width = isCollapsed ? sidebarCollapsedWidth : sidebarWidth

    const subNavRef = useRef<HTMLDivElement | null>(null)

    const fixMarginOffset = (): void => {
        if (!subNavRef.current) return

        subNavRef.current.style.marginTop = '0px'
        subNavRef.current.style.height = 'auto'
        const rect = subNavRef.current.getBoundingClientRect()
        subNavRef.current.style.marginTop = `calc(-${rect.top}px + ${appBarHeight})`
        subNavRef.current.style.height = `calc(${Math.max(rect.top, rect.height)}px - ${appBarHeight} + 42px)`
    }

    useLayoutEffect(() => {
        if (!subNavRef.current) return
        setSubNavRef(subNavRef.current)
        fixMarginOffset()
    }, [isCollapsed]) // eslint-disable-line react-hooks/exhaustive-deps

    if (!(navItem.subMenu && navItem.subMenu.length)) return null

    return (
        <Box
            ref={subNavRef}
            onTransitionEnd={fixMarginOffset}
            onMouseLeave={(): void => setSubNavForceHidden(false)}
            sx={{
                width: subNavWidth,
                position: 'absolute',
                top: 0,
                zIndex: 900,
                paddingBottom: 2,
                paddingTop: '1.4rem',
                left: width,
                ...getTransition('left'),
            }}
        >
            <Box
                sx={{
                    transform: 'translateY(-24px)',
                    opacity: 0,
                    ...getTransition('opacity, transform'),

                    '.hoverParent:hover &': {
                        opacity: 1,
                        transform: 'translateY(0px)',
                    },
                }}
            >
                <Box pl={3}>
                    <Typography component="div" variant="eyebrow" pl={2} mb={2}>{navItem.label}</Typography>
                </Box>

                {navItem.subMenu.map((item) =>
                    <CollapsingSidebarNavItem
                        key={item.pathname}
                        item={item}
                        isSubNavItem
                        setSubNavForceHidden={setSubNavForceHidden}
                    />)}
            </Box>
        </Box>
    )
}

const CollapsingSidebarToggleButton = (): React.ReactElement => {
    const [isCollapsed, setIsCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const intl = useIntl()
    const ariaLabel = isCollapsed
        ? intl.formatMessage({ id: 'components_sidebar_toggle_expand', defaultMessage: 'Expand sidebar' })
        : intl.formatMessage({ id: 'components_sidebar_toggle_collapse', defaultMessage: 'Collapse sidebar' })

    return <Box sx={{
        position: 'relative',
        zIndex: 1000,
        height: isCollapsed ? '30px' : '0',
        mb: isCollapsed ? 3 : 0,
        ...getTransition('height, margin'),
    }}
    >
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
                height: '30px',
                width: '30px',
                position: 'absolute',
                left: isCollapsed
                    ? (t): string => t.spacing(navCollapsedSurroundingPadding)
                    : '100%',
                transform: isCollapsed ? 'translateX(0%)' : 'translateX(-50%)',
                ...getTransition('left, transform'),
            }}
        >
            <C1IconButton
                onClick={(): void => setIsCollapsed(!isCollapsed)}
                variant="outlined"
                color="info"
                aria-label={ariaLabel}
                sx={{
                    height: isCollapsed ? '30px' : '20px',
                    width: isCollapsed ? '30px' : '20px',
                    borderColor: 'info.extraLight',
                    backgroundColor: 'background.default',
                    ...getTransition('height, width'),

                    '& .MuiSvgIcon-root': { transition: 'transform 0.8s' },

                    '&:hover': {
                        height: isCollapsed ? '30px' : '28px',
                        width: isCollapsed ? '30px' : '28px',
                        backgroundColor: 'background.default',

                        '& .MuiSvgIcon-root': {
                            transform: `translateX(${isCollapsed ? '3px' : '-3px'})`,
                        },
                    },
                }}
            >
                {isCollapsed
                    ? <C1Icon icon={ArrowRight} size="sm" />
                    : <C1Icon icon={ArrowLeft} size="sm" />}
            </C1IconButton>
        </Stack>
    </Box>
}

type CollapsingSidebarSafeAreaProps = {
    hoverItemRef: RefObject<HTMLDivElement>
    subNavRef: HTMLDivElement | null
    mousePosition: { x: number, y: number }
}

const safeAreaGrowBy = 0

const CollapsingSidebarSafeArea: FC<CollapsingSidebarSafeAreaProps> = ({ hoverItemRef, subNavRef, mousePosition }) => {
    const [isCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)

    const safeAreaRef = useRef<HTMLDivElement>(null)

    const hoverItemRect = hoverItemRef.current?.getBoundingClientRect()
    const subNavRect = subNavRef?.getBoundingClientRect()

    const padding = isCollapsed ? navCollapsedSurroundingPadding : navSurroundingPadding

    // Get the mouse position relative to the safe area ref
    const pos = safeAreaRef.current ? {
        x: mousePosition.x - safeAreaRef.current.getBoundingClientRect().left,
        y: mousePosition.y - safeAreaRef.current.getBoundingClientRect().top,
    } : { x: 0, y: 0 }

    if (!hoverItemRect || !subNavRect) return null

    return <Box
        ref={safeAreaRef}
        sx={{
            position: 'absolute',
            top: -safeAreaGrowBy,
            left: 0,
            right: -padding,
            height: subNavRect.height + (safeAreaGrowBy * 2),
            transform: `translateY(-${hoverItemRect.top - subNavRect.top}px)`,
            clipPath: `polygon(${pos.x}px ${pos.y}px, 100% 0, 100% 100%)`,
        }}
    />
}

const CollapsingSidebarBackdrop = (): React.ReactElement => {
    const [isCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const width = isCollapsed ? sidebarCollapsedWidth : sidebarWidth

    return <Box sx={{
        ...getTransition('left'),
        width: subNavWidth,
        borderLeft: '1px solid',
        borderColor: 'info.extraLight',
        left: width,
        height: '100vh',
        position: 'absolute',
        top: 0,
        backgroundColor: 'background.default',
        visibility: 'hidden',

        '.hoverParent:hover &': { visibility: 'visible' },

        '&:after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '100%',
            bottom: 0,
            width: '80px',
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.0))',
            pointerEvents: 'none',
        },
    }}
    />
}
